import React, { useState } from 'react';
import { Lock, Calendar, DollarSign, Settings, LogOut, Save, Check } from 'lucide-react';
import AvailabilityTab from './AvailabilityTab';
import PricingTab from './PricingTab';
import SettingsTab from './SettingsTab';

function getAdminPassword() {
  return localStorage.getItem('adminPassword') || 'villa2025';
}

export default function AdminPanel() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [activeTab, setActiveTab] = useState('availability');
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [saveMessage, setSaveMessage] = useState('');

  const handleLogin = (e) => {
    e.preventDefault();
    setLoginError('');
    if (loginForm.username === 'admin' && loginForm.password === getAdminPassword()) {
      setIsLoggedIn(true);
      sessionStorage.setItem('admin_logged_in', 'true');
    } else {
      setLoginError('Invalid username or password');
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    sessionStorage.removeItem('admin_logged_in');
  };

  const handleGlobalSave = () => {
    // Force a storage event so all contexts refresh
    window.dispatchEvent(new Event('storage'));
    setSaveMessage('Changes saved successfully');
    setTimeout(() => setSaveMessage(''), 3000);
  };

  if (!isLoggedIn) {
    return (
      <div className="admin-login-page" data-testid="admin-login">
        <form className="admin-login-card" onSubmit={handleLogin}>
          <div className="admin-login-header">
            <Lock className="h-8 w-8" />
            <h1>Villa in Pietra</h1>
            <p>Admin Panel</p>
          </div>
          {loginError && <div className="admin-error" data-testid="login-error">{loginError}</div>}
          <div className="admin-field">
            <label>Username</label>
            <input
              type="text"
              value={loginForm.username}
              onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
              data-testid="admin-username"
              autoComplete="username"
            />
          </div>
          <div className="admin-field">
            <label>Password</label>
            <input
              type="password"
              value={loginForm.password}
              onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
              data-testid="admin-password"
              autoComplete="current-password"
            />
          </div>
          <button type="submit" className="admin-login-btn" data-testid="admin-login-btn">
            Sign In
          </button>
        </form>
      </div>
    );
  }

  const tabs = [
    { id: 'availability', label: 'Availability', icon: Calendar },
    { id: 'pricing', label: 'Pricing', icon: DollarSign },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="admin-panel" data-testid="admin-panel">
      <header className="admin-header">
        <div className="admin-header-left">
          <h1>Villa in Pietra — Admin</h1>
        </div>
        <div className="admin-header-right">
          <button onClick={handleGlobalSave} className="admin-global-save-btn" data-testid="global-save-btn">
            {saveMessage ? <Check className="h-4 w-4" /> : <Save className="h-4 w-4" />}
            {saveMessage ? 'Saved!' : 'Save All Changes'}
          </button>
          <a href="/" className="admin-back-link">View Site</a>
          <button onClick={handleLogout} className="admin-logout-btn" data-testid="admin-logout">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </header>

      {saveMessage && (
        <div className="admin-save-toast" data-testid="save-toast">
          <Check className="h-5 w-5" />
          {saveMessage}
        </div>
      )}

      <nav className="admin-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`admin-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </nav>

      <main className="admin-content">
        {activeTab === 'availability' && <AvailabilityTab />}
        {activeTab === 'pricing' && <PricingTab />}
        {activeTab === 'settings' && <SettingsTab />}
      </main>
    </div>
  );
}
